/**
 * Poster Detector
 * Handles MindAR image detection and tracking
 */

import * as THREE from 'three';
import { PoseFilter } from './pose-filter.js';
import { MarkerPriorityCalculator } from '../services/marker-priority-calculator.js';
import { DeviceCapabilityDetector } from '../utils/performance-utils.js';
import { getDiagnostics } from '../utils/performance-diagnostics.js';

// Import MindAR from npm package instead of CDN
import 'mind-ar/dist/mindar-image-three.prod.js';

/**
 * Poster Detector class for MindAR integration
 */
export class PosterDetector {
  constructor(options = {}) {
    this.mindarThree = null;
    this.isInitialized = false;
    this.isDetecting = false;
    this.onDetectedCallback = null;
    this.onLostCallback = null;
    this.currentMatrix = null;
    this.animationFrameId = null;
    this.anchors = []; // Store multiple anchors for multi-target support
    this.maxTargets = options.maxTargets || 1; // Number of targets to track

    // Multi-marker support
    this.markerStates = new Map(); // markerId -> MarkerState
    this.targetIndexToMarkerId = new Map(); // targetIndex -> markerId
    this.markerConfig = null; // Store the loaded MarkerConfig

    // Platform detection for adaptive filtering
    this.platform = DeviceCapabilityDetector.getPlatform();
    console.log(`📱 Platform detected for AR tracking: ${this.platform}`);

    // Pose filtering for smooth tracking
    this.poseFilter = new PoseFilter({
      processNoise: options.processNoise || 0.01,
      measurementNoise: options.measurementNoise || 0.1,
      platform: this.platform, // Pass platform to filter
    });

    // Platform-specific smooth tracking
    // iOS: Enable Kalman filter for autofocus/stabilization noise
    // Android: Disable for faster response (d7bc855 baseline)
    this.smoothTracking = this.platform === 'iOS';
    console.log(
      `📊 Smooth tracking: ${this.smoothTracking ? 'enabled (iOS)' : 'disabled (Android - fast response)'}`
    );
  }

  /**
   * Initialize MindAR detector
   * @param {string} targetPath - Path to compiled .mind target file
   * @returns {Promise<void>}
   */
  async init(targetPath) {
    try {
      console.log('Initializing PosterDetector with target:', targetPath);

      // Check if MindAR is available (loaded via import)
      if (typeof window.MINDAR === 'undefined' || !window.MINDAR.IMAGE) {
        throw new Error('MindAR library not loaded from npm package');
      }

      // Create MindAR controller
      const { MindARThree } = window.MINDAR.IMAGE;

      // d7bc855: Optimized detection settings for fast response
      this.mindarThree = new MindARThree({
        container: document.getElementById('mindar-container'),
        imageTargetSrc: targetPath,
        maxTrack: this.maxTargets, // Dynamic based on marker count (1-5)
        uiLoading: 'no', // Disable default loading UI
        uiScanning: 'no', // Disable default scanning UI
        uiError: 'no', // Disable default error UI
        uiError: 'no', // Disable default error UI
        filterMinCF: this.platform === 'iOS' ? 0.0001 : 0.0001, // iOS: low cutoff for smoothing
        filterBeta: this.platform === 'iOS' ? 100 : 1000, // iOS: reduced to 100 to eliminate jitter (was 10000)
        missTolerance: 0, // Fast response on lost (default: 5)
        warmupTolerance: 0, // Fast startup (default: 5)
        // Maximum quality video settings (Full HD)
        videoSettings: {
          facingMode: 'environment',
          width: { ideal: 1920, min: 1280 },
          height: { ideal: 1080, min: 720 },
          frameRate: { ideal: 30, max: 60 },
        },
      });

      const { renderer, scene, camera } = this.mindarThree;

      // Store Three.js objects for external use
      this.renderer = renderer;
      this.scene = scene;
      this.camera = camera;

      // Setup anchors for all targets in the .mind file
      // MindAR .mind files can contain multiple targets
      for (let i = 0; i < this.maxTargets; i++) {
        const anchor = this.mindarThree.addAnchor(i);
        this.anchors.push({
          index: i,
          anchor: anchor,
          isTracking: false,
          matrix: null,
        });

        // Setup detection callbacks for this anchor
        anchor.onTargetFound = () => {
          const anchorData = this.anchors[i];

          // Performance diagnostics overlay (Android only)
          if (this.platform === 'Android') {
            getDiagnostics().logEvent('DETECTED', `Target ${i} detected`, {
              platform: this.platform,
              wasTracking: anchorData.isTracking,
            });
          }

          // Three.js optimization flags (set once on first detection)
          if (!anchorData.optimizationFlagsSet) {
            anchor.group.matrixAutoUpdate = false; // Manual matrix updates for performance
            anchor.group.frustumCulled = false; // Disable culling (AR markers always visible)
            anchorData.optimizationFlagsSet = true;
          }

          // T019: Calculate recovery time if previously lost
          let recoveryTime = null;
          if (anchorData.lastLostAt) {
            recoveryTime = performance.now() - anchorData.lastLostAt;
            if (recoveryTime <= 500) {
              console.log(
                `✅ Target ${i} recovered in ${recoveryTime.toFixed(0)}ms (goal: <500ms)`
              );
            } else {
              console.warn(
                `⚠️ Target ${i} recovered in ${recoveryTime.toFixed(0)}ms (slow recovery, goal: <500ms)`
              );
            }

            // T019: Reset pose filter if lost for >2 seconds
            if (recoveryTime > 2000 && this.smoothTracking) {
              console.log(
                `🔄 Target ${i} lost for ${(recoveryTime / 1000).toFixed(1)}s, resetting pose filter`
              );
              this.poseFilter.reset();
            }

            anchorData.lastLostAt = null; // Clear timestamp
          } else {
            console.log(`Target ${i} detected!`);
          }

          anchorData.isTracking = true;

          // Get raw matrix from MindAR
          const rawMatrix = anchor.group.matrix.clone();

          // Apply Kalman filtering for smooth tracking
          if (this.smoothTracking) {
            anchorData.matrix = this.poseFilter.filter(rawMatrix);
          } else {
            anchorData.matrix = rawMatrix;
          }

          // For backward compatibility, store first detected target in currentMatrix
          if (i === 0) {
            this.currentMatrix = anchorData.matrix;
          }

          if (this.onDetectedCallback) {
            this.onDetectedCallback(anchorData.matrix, i);
          }
        };

        anchor.onTargetLost = () => {
          console.log(`Target ${i} lost`);
          const anchorData = this.anchors[i];

          // Performance diagnostics overlay (Android only)
          if (this.platform === 'Android') {
            getDiagnostics().logEvent('LOST', `Target ${i} lost`, {
              platform: this.platform,
              wasTracking: anchorData.isTracking,
            });
          }

          anchorData.isTracking = false;
          anchorData.matrix = null;
          anchorData.lastLostAt = performance.now(); // T019: Record lost timestamp

          // For backward compatibility
          if (i === 0) {
            this.currentMatrix = null;
          }

          // Note: Don't reset pose filter immediately (T019)
          // Only reset if lost for >2 seconds (checked in onTargetFound)

          if (this.onLostCallback) {
            this.onLostCallback(i);
          }
        };
      }

      // For backward compatibility, keep reference to first anchor
      this.anchor = this.anchors[0]?.anchor;

      this.isInitialized = true;
      console.log('PosterDetector initialized successfully');
    } catch (error) {
      console.error('Failed to initialize PosterDetector:', error);
      throw error;
    }
  }

  /**
   * Start detection loop
   * @returns {Promise<void>}
   */
  async start() {
    if (!this.isInitialized) {
      throw new Error('PosterDetector not initialized. Call init() first.');
    }

    if (this.isDetecting) {
      console.warn('PosterDetector already running');
      return;
    }

    try {
      console.log('Starting MindAR tracker... (this may take 10-30 seconds on mobile devices)');

      // MindAR.start() initializes the tracker and loads the compiled target
      // This can be slow on mobile devices
      const startPromise = this.mindarThree.start();
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error('AR tracking initialization timeout after 60 seconds')),
          60000
        )
      );

      await Promise.race([startPromise, timeoutPromise]);

      // Extract target dimensions from MindAR's internal postMatrixs
      // MindAR computes postMatrixs from imageTargetDimensions: [markerWidth, markerHeight]
      // We can reverse-engineer the aspect ratio from the postMatrix transformation
      if (this.mindarThree.postMatrixs && this.mindarThree.postMatrixs.length > 0) {
        this.targetDimensions = [];

        for (let i = 0; i < this.mindarThree.postMatrixs.length; i++) {
          const postMatrix = this.mindarThree.postMatrixs[i];

          // Extract scale and position directly from matrix elements
          // postMatrix is a Three.js Matrix4
          // Scale is in elements [0], [5], [10]
          // Position is in elements [12], [13], [14]
          const scaleX = Math.sqrt(
            postMatrix.elements[0] * postMatrix.elements[0] +
              postMatrix.elements[1] * postMatrix.elements[1] +
              postMatrix.elements[2] * postMatrix.elements[2]
          );
          // const positionX = postMatrix.elements[12]; // Unused, kept for reference
          const positionY = postMatrix.elements[13];

          // MindAR formula (from three.js:217-226):
          // const [markerWidth, markerHeight] = imageTargetDimensions[i];
          // position.x = markerWidth / 2;
          // position.y = markerWidth / 2 + (markerHeight - markerWidth) / 2;
          // scale.x = markerWidth;

          // Reverse calculation:
          // markerWidth = scale.x
          // markerHeight = (position.y - markerWidth/2) * 2 + markerWidth
          const markerWidth = scaleX;
          const markerHeight = (positionY - markerWidth / 2) * 2 + markerWidth;

          this.targetDimensions.push({
            index: i,
            width: markerWidth,
            height: markerHeight,
            aspectRatio: markerWidth / markerHeight,
          });

          console.log(
            `📐 Target ${i}: ${markerWidth.toFixed(3)} x ${markerHeight.toFixed(3)} (aspect: ${(markerWidth / markerHeight).toFixed(3)})`
          );
        }
        console.log(`✅ Extracted ${this.targetDimensions.length} target dimension(s) from MindAR`);
      }

      // Debug: Log MindAR system state
      console.log('🔍 [MINDAR STATE] Checking MindAR system:', {
        'video exists': !!this.mindarThree.video,
        'video readyState': this.mindarThree.video?.readyState,
        'video playing': !this.mindarThree.video?.paused,
        'anchors count': this.anchors.length,
        maxTrack: this.maxTargets,
      });

      // Check if camera stream is active
      if (this.mindarThree.video) {
        console.log('📹 Camera feed:', {
          videoWidth: this.mindarThree.video.videoWidth,
          videoHeight: this.mindarThree.video.videoHeight,
        });
      }

      this.isDetecting = true;
      console.log('Poster detection started successfully');
    } catch (error) {
      console.error('Failed to start poster detection:', error);
      throw error;
    }
  }

  /**
   * Stop detection loop
   */
  stop() {
    if (!this.isDetecting) {
      return;
    }

    try {
      this.mindarThree.stop();
      this.isDetecting = false;
      console.log('Poster detection stopped');
    } catch (error) {
      console.error('Failed to stop poster detection:', error);
    }
  }

  /**
   * Get current detection matrix
   * @returns {THREE.Matrix4|null} - Current transformation matrix or null
   */
  getDetectionMatrix() {
    return this.currentMatrix;
  }

  /**
   * Check if poster is currently detected
   * @returns {boolean}
   */
  isDetected() {
    return this.currentMatrix !== null;
  }

  /**
   * Set detection callbacks
   * @param {Function} onDetected - Callback when poster is detected
   * @param {Function} onLost - Callback when poster is lost
   */
  setCallbacks(onDetected, onLost) {
    this.onDetectedCallback = onDetected;
    this.onLostCallback = onLost;
  }

  /**
   * Get Three.js renderer
   * @returns {THREE.WebGLRenderer}
   */
  getRenderer() {
    return this.renderer;
  }

  /**
   * Get Three.js scene
   * @returns {THREE.Scene}
   */
  getScene() {
    return this.scene;
  }

  /**
   * Get Three.js camera
   * @returns {THREE.Camera}
   */
  getCamera() {
    return this.camera;
  }

  /**
   * Get MindAR anchor for adding 3D objects (first anchor)
   * @returns {object}
   */
  getAnchor() {
    return this.anchor;
  }

  /**
   * Get specific anchor by index
   * @param {number} index - Target index
   * @returns {object|null}
   */
  getAnchorByIndex(index) {
    return this.anchors[index]?.anchor || null;
  }

  /**
   * Get all anchors
   * @returns {Array}
   */
  getAllAnchors() {
    return this.anchors;
  }

  /**
   * Get target dimensions extracted from MindAR
   * Returns array of {index, width, height, aspectRatio} for each target
   * @returns {Array|null}
   */
  getTargetDimensions() {
    return this.targetDimensions;
  }

  /**
   * Get aspect ratio for specific target
   * @param {number} index - Target index (default: 0)
   * @returns {number|null} - Aspect ratio (width/height) or null if not available
   */
  getTargetAspectRatio(index = 0) {
    if (!this.targetDimensions || !this.targetDimensions[index]) {
      return null;
    }
    return this.targetDimensions[index].aspectRatio;
  }

  /**
   * Enable or disable smooth tracking
   * @param {boolean} enabled - Enable smooth tracking
   */
  setSmoothTracking(enabled) {
    this.smoothTracking = enabled;
    console.log(`Smooth tracking ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Calculate adaptive measurement noise based on marker distance
   * Platform-specific strategies for optimal tracking performance
   * @param {THREE.Matrix4} matrix - Pose matrix
   * @returns {number} - Adaptive measurement noise
   * @private
   */
  _calculateAdaptiveMeasurementNoise(matrix) {
    const position = new THREE.Vector3();
    position.setFromMatrixPosition(matrix);
    const distance = Math.abs(position.z);

    // Android: 1f24d18 baseline formula for responsive tracking
    if (this.platform === 'Android') {
      // Distance thresholds (1f24d18 proven range)
      const minDistance = 0.3; // Close range
      const maxDistance = 2.0; // Far range

      // Calculate ratio (0=close, 1=far)
      const distanceRatio = Math.min(
        Math.max((distance - minDistance) / (maxDistance - minDistance), 0),
        1
      );

      // 1f24d18 formula: 0.04 (close) → 0.01 (far)
      // Lighter filtering = faster response = better Android UX
      return 0.04 - distanceRatio * 0.03;
    }

    // iOS: Optimized for ultra-close range autofocus compensation
    if (this.platform === 'iOS') {
      // Ultra-close range: Maximum filtering for autofocus noise
      if (distance < 0.25) {
        return 0.15; // iOS autofocus creates extreme noise at <25cm (increased from 0.10)
      }

      // Normal range: Balanced filtering
      const minDistance = 0.25;
      const maxDistance = 2.0;
      const distanceRatio = Math.min(
        Math.max((distance - minDistance) / (maxDistance - minDistance), 0),
        1
      );

      // 0.08 (close) → 0.03 (far) - slightly increased for better stability
      return 0.08 - distanceRatio * 0.05;
    }

    // Desktop: Balanced baseline
    const minDistance = 0.3;
    const maxDistance = 2.0;
    const distanceRatio = Math.min(
      Math.max((distance - minDistance) / (maxDistance - minDistance), 0),
      1
    );
    return 0.05 - distanceRatio * 0.03; // 0.05 → 0.02
  }

  /**
   * Update pose filter parameters
   * @param {number} processNoise - Process noise covariance
   * @param {number} measurementNoise - Measurement noise covariance
   */
  updateFilterParameters(processNoise, measurementNoise) {
    if (this.poseFilter) {
      this.poseFilter.updateParameters(processNoise, measurementNoise);
    }
  }

  /**
   * Initialize with multiple markers configuration
   * @param {Object} markerConfig - MarkerConfig object from MarkerConfigLoader
   * @returns {Promise<void>}
   */
  async initializeMultipleMarkers(markerConfig) {
    if (!this.isInitialized) {
      throw new Error(
        'PosterDetector must be initialized first. Call init() before initializeMultipleMarkers().'
      );
    }

    this.markerConfig = markerConfig;

    // Verify anchor count matches marker count
    if (this.anchors.length !== markerConfig.markers.length) {
      throw new Error(
        `Anchor count mismatch: .mind file has ${this.anchors.length} targets, ` +
          `but config defines ${markerConfig.markers.length} markers`
      );
    }

    // Create MarkerState for each marker
    markerConfig.markers.forEach((markerDef, _index) => {
      const anchorData = this.anchors[markerDef.targetIndex];

      if (!anchorData) {
        throw new Error(`No anchor found for targetIndex ${markerDef.targetIndex}`);
      }

      const markerState = {
        markerId: markerDef.id,
        anchorIndex: markerDef.targetIndex,
        trackingStatus: 'not_found',
        anchor: anchorData.anchor,
        poseMatrix: null,
        screenPosition: null, // T012: {x, y} in pixels
        screenSize: null,
        distanceToCenter: null, // T012: Distance from viewport center in pixels
        priorityScore: Infinity,
        lastDetectedAt: null,
        actionButton: null, // Initialize actionButton slot
        timeSinceLost: 0,
        markerDefinition: markerDef,
      };

      this.markerStates.set(markerDef.id, markerState);
      this.targetIndexToMarkerId.set(markerDef.targetIndex, markerDef.id);
    });

    console.log(`✅ Initialized ${this.markerStates.size} marker states`);
  }

  /**
   * Get all marker states
   * @returns {Array<Object>} Array of MarkerState objects
   */
  getAllMarkerStates() {
    return Array.from(this.markerStates.values());
  }

  /**
   * Get marker state by ID
   * @param {string} markerId - Marker ID
   * @returns {Object|null} MarkerState or null if not found
   */
  getMarkerState(markerId) {
    return this.markerStates.get(markerId) || null;
  }

  /**
   * Get marker state by target index
   * @param {number} targetIndex - Target index
   * @returns {Object|null} MarkerState or null if not found
   */
  getMarkerStateByTargetIndex(targetIndex) {
    const markerId = this.targetIndexToMarkerId.get(targetIndex);
    return markerId ? this.markerStates.get(markerId) : null;
  }

  /**
   * Update marker state from anchor data
   * @param {number} targetIndex - Target index
   * @param {Object} anchorData - Anchor data from MindAR
   */
  updateMarkerState(targetIndex, anchorData) {
    const markerState = this.getMarkerStateByTargetIndex(targetIndex);
    if (!markerState) {
      console.warn(`🔍 [UPDATE DEBUG] No marker state found for targetIndex ${targetIndex}`);
      return;
    }

    // Check anchor visibility directly (more reliable than event-based isTracking)
    const isCurrentlyVisible = anchorData.anchor?.group?.visible === true;

    // Debug: Check anchor internals periodically (every 120 frames = ~2 seconds) for first marker only
    if (targetIndex === 0 && Math.random() < 0.008) {
      // ~1/120 chance
      console.log(`🔍 [ANCHOR STATE]`, {
        targetIndex: targetIndex,
        'group.visible': anchorData.anchor?.group?.visible,
        isTracking: anchorData.isTracking,
        hasMatrix: !!anchorData.matrix,
      });
    }

    // Update isTracking based on actual visibility (fallback if events don't fire)
    if (isCurrentlyVisible && !anchorData.isTracking) {
      console.log(
        `✅ [TRACKING FIX] ${markerState.markerId}: Setting isTracking=true based on group.visible`
      );
      anchorData.isTracking = true;
      // CRITICAL FIX: Removed duplicate Kalman filtering from updateMarkerState()
      // Filtering is handled ONLY in onTargetFound callback for consistency
      // This eliminates double-filtering that caused iOS jitter and lag
      if (anchorData.anchor?.group?.matrix) {
        anchorData.matrix = anchorData.anchor.group.matrix.clone();
      }
    } else if (!isCurrentlyVisible && anchorData.isTracking) {
      console.log(
        `❌ [TRACKING FIX] ${markerState.markerId}: Setting isTracking=false based on group.visible`
      );
      anchorData.isTracking = false;
      anchorData.matrix = null;
    }

    if (anchorData.isTracking) {
      markerState.trackingStatus = 'tracking';
      markerState.poseMatrix = anchorData.matrix;
      markerState.lastDetectedAt = Date.now();
      markerState.timeSinceLost = 0;

      // Calculate screen position and size if camera is available
      if (this.camera && markerState.anchor && markerState.anchor.group) {
        // Extract position from world matrix (MindAR sets matrix, not position directly)
        const worldPosition = new THREE.Vector3();
        markerState.anchor.group.getWorldPosition(worldPosition);

        // Project to screen space
        const screenPosition = worldPosition.clone();
        screenPosition.project(this.camera);

        markerState.screenPosition = {
          x: screenPosition.x,
          y: screenPosition.y,
        };

        // T012: Calculate distance from viewport center in pixels
        if (this.renderer) {
          const viewport = {
            width: this.renderer.domElement.width,
            height: this.renderer.domElement.height,
          };
          markerState.distanceToCenter = MarkerPriorityCalculator.calculateScreenDistance(
            worldPosition,
            this.camera,
            viewport
          );
        } else {
          // Fallback: use normalized distance if no renderer
          markerState.distanceToCenter = Math.sqrt(
            screenPosition.x * screenPosition.x + screenPosition.y * screenPosition.y
          );
        }

        // Extract scale from matrix
        const worldScale = new THREE.Vector3();
        markerState.anchor.group.getWorldScale(worldScale);
        markerState.screenSize = worldScale.length();

        // Periodic debug log (every ~2 seconds)
        if (Math.random() < 0.008) {
          console.log(
            `🔍 [MARKER POS] ${markerState.markerId}: world=(${worldPosition.x.toFixed(2)}, ${worldPosition.y.toFixed(2)}, ${worldPosition.z.toFixed(2)}), screen=(${screenPosition.x.toFixed(2)}, ${screenPosition.y.toFixed(2)}), distToCenter=${markerState.distanceToCenter?.toFixed(1)}px, size=${markerState.screenSize.toFixed(2)}`
          );
        }
      } else {
        console.warn(
          `🔍 [UPDATE DEBUG] ${markerState.markerId}: Cannot calculate screen position (camera=${!!this.camera}, anchor=${!!markerState.anchor}, group=${!!markerState.anchor?.group})`
        );
      }
    } else {
      // const wasTracking = markerState.trackingStatus === 'tracking'; // Unused, kept for future debugging
      markerState.trackingStatus = 'lost';
      markerState.poseMatrix = null;
      markerState.screenPosition = null;
      markerState.screenSize = null;
      markerState.distanceToCenter = null; // T012: Clear distance when lost

      if (markerState.lastDetectedAt) {
        markerState.timeSinceLost = Date.now() - markerState.lastDetectedAt;

        // Reset to not_found after 2 seconds (SC-AR-003)
        if (markerState.timeSinceLost > 2000) {
          markerState.trackingStatus = 'not_found';
          markerState.lastDetectedAt = null;
        }
      }
    }
  }

  /**
   * Get currently tracking marker states
   * @returns {Array<Object>} Array of tracking MarkerState objects
   */
  getTrackingMarkerStates() {
    return this.getAllMarkerStates().filter((state) => state.trackingStatus === 'tracking');
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.stop();

    if (this.mindarThree) {
      // MindAR cleanup
      this.mindarThree.stop();
      this.mindarThree = null;
    }

    // Reset pose filter
    if (this.poseFilter) {
      this.poseFilter.reset();
    }

    // Clear marker states
    this.markerStates.clear();
    this.targetIndexToMarkerId.clear();
    this.markerConfig = null;

    this.isInitialized = false;
    this.currentMatrix = null;
    this.onDetectedCallback = null;
    this.onLostCallback = null;

    console.log('PosterDetector cleaned up');
  }
}
